import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { dashboardTheme } from '../styles/dashboardTheme';
import { formatDistanceToNow } from 'date-fns';
import WidgetInfoTooltip from './WidgetInfoTooltip';

/**
 * SessionSummaryWidget Component
 * Shows the latest therapy session summary with reflection prompts
 */
const SessionSummaryWidget = ({ lastSession, onViewSummary }) => {
  console.log('SessionSummaryWidget received lastSession:', lastSession);
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: dashboardTheme.shadows.medium,
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          boxShadow: dashboardTheme.shadows.large
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Session Summary
          </Typography>
          <WidgetInfoTooltip id="sessionSummary" />
        </Box>
        {lastSession && lastSession.createdAt && (
          <Chip
            icon={<CalendarTodayIcon sx={{ fontSize: '0.9rem' }} />}
            label={formatDistanceToNow(new Date(lastSession.createdAt), { addSuffix: true })}
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        )}
      </Box>

      {lastSession ? (
        <Box sx={{ flex: 1 }}>
          <Box sx={{ 
            p: 2, 
            mb: 2,
            background: 'linear-gradient(135deg, rgba(66, 165, 245, 0.05) 0%, rgba(66, 165, 245, 0.02) 100%)',
            borderRadius: '12px',
            border: '1px solid rgba(66, 165, 245, 0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <SummarizeIcon sx={{ fontSize: '1rem', color: '#42a5f5' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Session Insights
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ 
              color: '#1a1a1a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.6
            }}>
              {lastSession.clientSummary || lastSession.sessionSummary || lastSession.summary || 'Your session insights will appear here after your next therapy conversation.'}
            </Typography>
          </Box>

          {lastSession.reflectionPrompts && lastSession.reflectionPrompts.length > 0 && (
            <Box sx={{ 
              mt: 2,
              p: 1.5,
              background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.05) 0%, rgba(251, 146, 60, 0.02) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(251, 146, 60, 0.15)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: '1rem', color: dashboardTheme.colors.primary.main }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: dashboardTheme.colors.primary.main }}>
                  Reflect on This
                </Typography>
                {lastSession.reflectionPrompts.length > 2 && (
                  <Chip 
                    label={`${lastSession.reflectionPrompts.length} prompts`}
                    size="small"
                    sx={{ 
                      ml: 'auto',
                      height: '20px',
                      fontSize: '0.7rem',
                      background: dashboardTheme.colors.primary.main + '15',
                      color: dashboardTheme.colors.primary.main
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {lastSession.reflectionPrompts.slice(0, 2).map((prompt, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      gap: 0.75
                    }}
                  >
                    <PsychologyIcon sx={{ 
                      fontSize: '0.875rem', 
                      color: dashboardTheme.colors.primary.main,
                      mt: 0.25,
                      opacity: 0.7
                    }} />
                    <Typography variant="body2" sx={{ 
                      color: '#1a1a1a',
                      flex: 1,
                      lineHeight: 1.5,
                      fontSize: '0.875rem'
                    }}>
                      {prompt}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2
          }}
        >
          <Box sx={{
            p: 2,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1) 0%, rgba(251, 146, 60, 0.05) 100%)',
            mb: 2
          }}>
            <PsychologyIcon sx={{ fontSize: 36, color: dashboardTheme.colors.primary.main, opacity: 0.8 }} />
          </Box>
          <Typography variant="body1" align="center" gutterBottom sx={{ fontWeight: 500, color: '#1a1a1a' }}>
            Your Journey Begins Here
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: '#64748b', lineHeight: 1.5 }}>
            After your therapy sessions, you'll find personalized insights and reflection prompts to deepen your growth
          </Typography>
        </Box>
      )}

      <Button
        variant={lastSession ? "outlined" : "contained"}
        fullWidth
        startIcon={<SummarizeIcon />}
        onClick={onViewSummary}
        sx={{ 
          mt: 2,
          borderRadius: '12px',
          background: lastSession ? 'transparent' : dashboardTheme.colors.info.gradient,
          '&:hover': {
            background: lastSession ? dashboardTheme.colors.info.main + '10' : dashboardTheme.colors.info.dark
          }
        }}
      >
        {lastSession ? 'View Full Summary' : 'Start Your First Session'}
      </Button>
    </Paper>
  );
};

export default SessionSummaryWidget;